import React, { useCallback, useState } from 'react';

import { Helmet } from 'react-helmet';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useCreateBundle } from '~/hooks/apis/accommodation/useBundles';
import useAppNavigation from '~/hooks/useAppNavigation';

import { CreateBundlePayload } from '~/services/AccommodationService';

import BundleLpcRatePlanIdsPicker from './components/BundleLpcRatePlanIdsPicker';
import BundlePropertyPicker, { PropertyOption } from './components/BundlePropertyPicker';
import {
  ALL_BEDBANK_SUPPLIERS,
  ALL_BOARD_CODES,
  ALL_BUNDLE_STATUSES,
  ALL_CANCELLATION_POLICIES,
  BedbankSupplier,
} from './constants';

interface OccupancyRow {
  // Maximums; '' (blank) means no upper bound when saved.
  adults: number | '';
  children: number | '';
  infants: number | '';
  // Minimums; '' (blank) means no lower bound (treated as 0) when saved.
  minAdults: number | '';
  minChildren: number | '';
  minInfants: number | '';
}

interface FormState {
  name: string;
  merchandisingLabel: string;
  status: NonNullable<CreateBundlePayload['status']>;
  optIn: boolean;
  appliesToLeadRate: boolean;
  properties: Array<PropertyOption>;
  los: number | '';
  leadDays: number | '';
  boardCodes: Array<CreateBundlePayload['rule']['boardCodes'][number]>;
  cancellationPolicies: Array<CreateBundlePayload['rule']['cancellationPolicies'][number]>;
  lpcRatePlanIds: Array<string>;
  allowAllLpcRatePlans: boolean;
  appliesToLpc: boolean;
  appliesToLpp: boolean;
  lppSupplierAllowlist: Array<BedbankSupplier>;
  includedGuests: Array<OccupancyRow>;
}

const INITIAL_STATE: FormState = {
  name: '',
  merchandisingLabel: '',
  status: 'draft',
  optIn: false,
  appliesToLeadRate: false,
  properties: [],
  los: 1,
  leadDays: 0,
  boardCodes: ['RO', 'BB', 'HB', 'FB', 'AI'],
  cancellationPolicies: ['RF', 'NRF'],
  lpcRatePlanIds: [],
  allowAllLpcRatePlans: false,
  appliesToLpc: false,
  appliesToLpp: false,
  lppSupplierAllowlist: [],
  includedGuests: [
    {
      adults: 2,
      children: 2,
      infants: 1,
      minAdults: '',
      minChildren: '',
      minInfants: '',
    },
  ],
};

let nextRowKey = 0;
const allocateRowKey = () => `row-${++nextRowKey}`;

function AccommodationBundleCreatePage() {
  const { push } = useAppNavigation();
  const [state, setState] = useState<FormState>(INITIAL_STATE);
  const [rowKeys, setRowKeys] = useState<Array<string>>(() => INITIAL_STATE.includedGuests.map(allocateRowKey));
  const [conflictPropertyIds, setConflictPropertyIds] = useState<Array<string>>([]);

  const createBundleMutation = useCreateBundle();

  const handleChange = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateOccupancyRow = useCallback((index: number, key: keyof OccupancyRow, value: number | '') => {
    setState((prev) => ({
      ...prev,
      includedGuests: prev.includedGuests.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    }));
  }, []);

  const addOccupancyRow = useCallback(() => {
    setState((prev) => ({
      ...prev,
      includedGuests: [
        ...prev.includedGuests,
        {
          adults: '',
          children: '',
          infants: '',
          minAdults: '',
          minChildren: '',
          minInfants: '',
        },
      ],
    }));
    setRowKeys((prev) => [...prev, allocateRowKey()]);
  }, []);

  const removeOccupancyRow = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      includedGuests: prev.includedGuests.filter((_, i) => i !== index),
    }));
    setRowKeys((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setConflictPropertyIds([]);

      const propertyIds = state.properties.map((p) => p.id);

      const payload: CreateBundlePayload = {
        name: state.name,
        // Blank means "no label": the customer-facing promotion name falls back to the
        // underlying promo's name until a label is set.
        ...(state.merchandisingLabel.trim() ? { merchandisingLabel: state.merchandisingLabel.trim() } : {}),
        status: state.status,
        optIn: state.optIn,
        appliesToLeadRate: state.appliesToLeadRate,
        propertyIds,
        rule: {
          los: typeof state.los === 'number' ? state.los : 0,
          leadDays: typeof state.leadDays === 'number' ? state.leadDays : 0,
          boardCodes: state.boardCodes,
          cancellationPolicies: state.cancellationPolicies,
          lpcRatePlanIds: state.lpcRatePlanIds,
          allowAllLpcRatePlans: state.allowAllLpcRatePlans,
          appliesToLpc: state.appliesToLpc,
          appliesToLpp: state.appliesToLpp,
          lppSupplierAllowlist: state.lppSupplierAllowlist,
          includedGuests: state.includedGuests.map((row) => ({
            // Blank inputs are omitted so absence carries "no bound" semantics:
            // no max => no upper bound, no min => no floor.
            ...(typeof row.adults === 'number' ? { adults: row.adults } : {}),
            ...(typeof row.children === 'number' ? { children: row.children } : {}),
            ...(typeof row.infants === 'number' ? { infants: row.infants } : {}),
            ...(typeof row.minAdults === 'number' ? { minAdults: row.minAdults } : {}),
            ...(typeof row.minChildren === 'number' ? { minChildren: row.minChildren } : {}),
            ...(typeof row.minInfants === 'number' ? { minInfants: row.minInfants } : {}),
          })),
          perOccupant: false,
          perNight: false,
        },
      };

      try {
        const result = await createBundleMutation.mutateAsync({
          payload,
          errorBodyCallback: (body) => {
            const conflicts =
              (body as { result?: { conflictingPropertyIds?: Array<string> } } | undefined)?.result
                ?.conflictingPropertyIds ?? [];
            if (conflicts.length > 0) {
              setConflictPropertyIds(conflicts);
            }
          },
        });
        push(`/accommodation/bundles/${result.bundleId}`);
      } catch {
        // Non-409 errors are surfaced via the hook's snackbar; 409 conflicts render inline above.
      }
    },
    [state, createBundleMutation, push],
  );

  const isSubmitting = createBundleMutation.isPending;
  const canSubmit =
    !!state.name.trim() &&
    state.properties.length > 0 &&
    state.boardCodes.length > 0 &&
    state.cancellationPolicies.length > 0;

  return (
    <Container maxWidth="md">
      <Helmet>
        <title>Create Bundle</title>
      </Helmet>
      <PageHeader title="Create Bundle" backButton="/accommodation/properties#bundles" />

      <Box component="form" onSubmit={handleSubmit} mt={2}>
        {conflictPropertyIds.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            One or more property IDs are already attached to another bundle: {conflictPropertyIds.join(', ')}
          </Alert>
        )}

        <Stack spacing={2}>
          <TextField
            label="Bundle Name"
            value={state.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Merchandising label"
            value={state.merchandisingLabel}
            onChange={(e) => handleChange('merchandisingLabel', e.target.value)}
            helperText="Shown to customers as the promotion name on offer tiles (e.g. “Dreamworld Bundle”). The bundle name above is internal only and never shown to customers."
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={state.status}
              onChange={(e) => handleChange('status', e.target.value as NonNullable<CreateBundlePayload['status']>)}
            >
              {ALL_BUNDLE_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <FormControlLabel
              control={<Checkbox checked={state.optIn} onChange={(e) => handleChange('optIn', e.target.checked)} />}
              label="Opt-in bundle"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              When ticked, the bundled experience is offered as a default-off add-on on the customer page (customer must
              tick to add). When unticked, the bundle is mandatory and baked into the rate price.
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.appliesToLeadRate}
                  onChange={(e) => handleChange('appliesToLeadRate', e.target.checked)}
                />
              }
              label="Applies to all rates (except GHA)"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              When ticked, the bundle can attach to the cheapest (lead) rate on the property. When unticked, the lead
              rate is excluded from bundling so comparison / metasearch surfaces (e.g. Google Hotel Ads) show un-bundled
              prices.
            </Typography>
          </Box>

          <BundlePropertyPicker value={state.properties} onChange={(next) => handleChange('properties', next)} />

          <Box>
            <Box mb={1} fontWeight={500}>
              Hotel Rule
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Min length of stay (nights)"
                  type="number"
                  value={state.los}
                  onChange={(e) => handleChange('los', e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Lead days"
                  type="number"
                  value={state.leadDays}
                  onChange={(e) => handleChange('leadDays', e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Board Codes</InputLabel>
                  <Select
                    multiple
                    label="Board Codes"
                    value={state.boardCodes}
                    onChange={(e) => handleChange('boardCodes', e.target.value as FormState['boardCodes'])}
                  >
                    {ALL_BOARD_CODES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Cancellation Policies</InputLabel>
                  <Select
                    multiple
                    label="Cancellation Policies"
                    value={state.cancellationPolicies}
                    onChange={(e) =>
                      handleChange('cancellationPolicies', e.target.value as FormState['cancellationPolicies'])
                    }
                  >
                    {ALL_CANCELLATION_POLICIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={0.5}>
                  Product types
                </Typography>
                <Box display="flex" gap={2}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.appliesToLpc}
                        onChange={(e) => handleChange('appliesToLpc', e.target.checked)}
                      />
                    }
                    label="Apply to LPC"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.appliesToLpp}
                        onChange={(e) => handleChange('appliesToLpp', e.target.checked)}
                      />
                    }
                    label="Apply to LPP"
                  />
                </Box>
                <Typography
                  variant="caption"
                  color={!state.appliesToLpc && !state.appliesToLpp ? 'warning.main' : 'text.secondary'}
                  display="block"
                >
                  {!state.appliesToLpc && !state.appliesToLpp
                    ? 'Both unticked — this bundle will not attach to any rate until you tick LPC and/or LPP.'
                    : 'Bundle only attaches to rate types that are ticked.'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={0.5}>
                  LPP suppliers
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.lppSupplierAllowlist.length === ALL_BEDBANK_SUPPLIERS.length}
                      indeterminate={
                        state.lppSupplierAllowlist.length > 0 &&
                        state.lppSupplierAllowlist.length < ALL_BEDBANK_SUPPLIERS.length
                      }
                      onChange={(e) =>
                        handleChange('lppSupplierAllowlist', e.target.checked ? [...ALL_BEDBANK_SUPPLIERS] : [])
                      }
                    />
                  }
                  label="All suppliers"
                />
                <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                  {ALL_BEDBANK_SUPPLIERS.map((supplier) => (
                    <FormControlLabel
                      key={supplier}
                      control={
                        <Checkbox
                          size="small"
                          checked={state.lppSupplierAllowlist.includes(supplier)}
                          onChange={(e) => {
                            const next = e.target.checked
                              ? [...state.lppSupplierAllowlist, supplier]
                              : state.lppSupplierAllowlist.filter((s) => s !== supplier);
                            handleChange('lppSupplierAllowlist', next);
                          }}
                        />
                      }
                      label={supplier}
                    />
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  color={
                    state.appliesToLpp && state.lppSupplierAllowlist.length === 0 ? 'warning.main' : 'text.secondary'
                  }
                  display="block"
                >
                  {!state.appliesToLpp
                    ? 'Enable "Apply to LPP" to restrict by supplier.'
                    : state.lppSupplierAllowlist.length === 0
                      ? 'No suppliers ticked — this bundle will not attach to any LPP rate.'
                      : 'Bundle attaches to LPP rates whose winning supplier is ticked above.'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.allowAllLpcRatePlans}
                      onChange={(e) => handleChange('allowAllLpcRatePlans', e.target.checked)}
                    />
                  }
                  label="Allow all LPC rate plans (bypass allowlist)"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  When ticked, the bundle attaches to every matching LPC rate regardless of the allowlist below.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <BundleLpcRatePlanIdsPicker
                  value={state.lpcRatePlanIds}
                  onChange={(next) => handleChange('lpcRatePlanIds', next)}
                  propertyIds={state.properties.map((p) => p.id)}
                  disabled={state.allowAllLpcRatePlans}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={1}>
                  Occupancies
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  A rate must satisfy at least one of these rows to be eligible. Each row sets optional MIN and/or MAX
                  per guest type independently — leave a field empty to omit that bound (no minimum floor, or no maximum
                  ceiling). Add multiple rows to express OR semantics.
                </Typography>
                <Stack spacing={2}>
                  {state.includedGuests.map((row, index) => (
                    <Box key={rowKeys[index]} border={1} borderColor="divider" borderRadius={1} p={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2">Occupancy #{index + 1}</Typography>
                        <IconButton
                          onClick={() => removeOccupancyRow(index)}
                          size="small"
                          aria-label="Remove occupancy row"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Stack spacing={2}>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <TextField
                              label="Min Adults"
                              type="number"
                              value={row.minAdults}
                              onChange={(e) =>
                                updateOccupancyRow(
                                  index,
                                  'minAdults',
                                  e.target.value === '' ? '' : Number(e.target.value),
                                )
                              }
                              inputProps={{ min: 0 }}
                              helperText="Leave blank for no minimum"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              label="Min Children"
                              type="number"
                              value={row.minChildren}
                              onChange={(e) =>
                                updateOccupancyRow(
                                  index,
                                  'minChildren',
                                  e.target.value === '' ? '' : Number(e.target.value),
                                )
                              }
                              inputProps={{ min: 0 }}
                              helperText="Leave blank for no minimum"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              label="Min Infants"
                              type="number"
                              value={row.minInfants}
                              onChange={(e) =>
                                updateOccupancyRow(
                                  index,
                                  'minInfants',
                                  e.target.value === '' ? '' : Number(e.target.value),
                                )
                              }
                              inputProps={{ min: 0 }}
                              helperText="Leave blank for no minimum"
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                          <Grid item xs={4}>
                            <TextField
                              label="Max Adults"
                              type="number"
                              value={row.adults}
                              onChange={(e) =>
                                updateOccupancyRow(index, 'adults', e.target.value === '' ? '' : Number(e.target.value))
                              }
                              inputProps={{ min: 0 }}
                              helperText="Leave blank for no maximum"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              label="Max Children"
                              type="number"
                              value={row.children}
                              onChange={(e) =>
                                updateOccupancyRow(
                                  index,
                                  'children',
                                  e.target.value === '' ? '' : Number(e.target.value),
                                )
                              }
                              inputProps={{ min: 0 }}
                              helperText="Leave blank for no maximum"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              label="Max Infants"
                              type="number"
                              value={row.infants}
                              onChange={(e) =>
                                updateOccupancyRow(
                                  index,
                                  'infants',
                                  e.target.value === '' ? '' : Number(e.target.value),
                                )
                              }
                              inputProps={{ min: 0 }}
                              helperText="Leave blank for no maximum"
                              fullWidth
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
                <Box mt={2}>
                  <Button onClick={addOccupancyRow} variant="outlined">
                    + Add occupancy
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button onClick={() => push('/accommodation/properties#bundles')}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Creating…' : 'Create Bundle'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
}

export default AccommodationBundleCreatePage;
